import { Link } from 'react-router-dom';
import { BookOpen, Link2, Scale, ShieldCheck, Users } from 'lucide-react';

import { countPartesByPolo, type ProcessoJudicialResponse } from '@/api/processual';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Spinner } from '@/components/ui/spinner';
import { classifyError } from '@/lib/errors';
import { acaoErroCertificado, CODIGO_CERT_EXPIRADO } from '@/lib/errors/certificado';
import { formatPartesResumo } from '@/lib/partes/resumo';
import { useAbrirAutos } from '@/hooks/useAbrirAutos';

interface ConsultaProcessualCardProps {
  processo: ProcessoJudicialResponse | undefined;
  isLoading: boolean;
  /** 202 em andamento: carga inicial disparada no backend, polling ativo. */
  isPreparing?: boolean;
  /** O polling do 202 esgotou a janela sem o preparo terminar. */
  prepareTimedOut?: boolean;
  /** Reabre a janela de polling e refaz a consulta (estado prepareTimedOut). */
  onRetry?: () => void;
  error: unknown;
  isTracked: boolean;
  searchQuery: string;
  onAcompanhar: () => void;
  onAcompanharLoading: boolean;
}

function ConsultandoCard() {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3 py-1">
          <Spinner size={32} label="Consultando o processo nos tribunais" />
          <div className="flex flex-col gap-0.5">
            <span className="text-sm font-medium">
              Consultando o processo nos tribunais…
            </span>
            <span className="text-sm text-muted-foreground">
              A primeira consulta pode levar alguns instantes.
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function ErrorContent({ error }: { error: unknown }) {
  const classified = classifyError(error);

  if (classified.kind === 'certificate_required') {
    return (
      <div className="flex items-start gap-3 py-3">
        <ShieldCheck className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
        <div className="flex flex-col gap-1">
          <span className="text-sm text-muted-foreground">
            {classified.codigo === CODIGO_CERT_EXPIRADO
              ? classified.message
              : 'Certificado digital necessário para consultar processos no CNJ e nos tribunais.'}
          </span>
          <Link
            to="/app/certificados"
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            {acaoErroCertificado(classified.codigo)}
          </Link>
        </div>
      </div>
    );
  }

  if (classified.kind === 'pdpj_required') {
    return (
      <div className="flex items-start gap-3 py-3">
        <Link2 className="h-5 w-5 text-muted-foreground shrink-0 mt-0.5" />
        <div className="flex flex-col gap-1">
          <span className="text-sm text-muted-foreground">Conecte-se à PDPJ para consultar processos no CNJ e nos tribunais.</span>
          <Link
            to="/app/certificados"
            className="text-sm font-medium text-primary underline-offset-4 hover:underline"
          >
            Conectar-se à PDPJ
          </Link>
        </div>
      </div>
    );
  }

  if (classified.kind === 'not_found') {
    return (
      <p className="text-sm text-muted-foreground">
        Processo não encontrado na consulta externa
      </p>
    );
  }

  if (classified.kind === 'service_unavailable' || classified.kind === 'server') {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Serviço de consulta processual temporariamente indisponível
        </AlertDescription>
      </Alert>
    );
  }

  if (classified.kind === 'rate_limited') {
    return (
      <Alert variant="destructive">
        <AlertDescription>
          Limite de consultas atingido. Aguarde antes de tentar novamente.
        </AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert variant="destructive">
      <AlertDescription>{classified.message}</AlertDescription>
    </Alert>
  );
}

export function ConsultaProcessualCard({
  processo,
  isLoading,
  isPreparing = false,
  prepareTimedOut = false,
  onRetry,
  error,
  isTracked,
  searchQuery,
  onAcompanhar,
  onAcompanharLoading,
}: ConsultaProcessualCardProps) {
  const abrirAutos = useAbrirAutos();

  const partiesCount = processo
    ? countPartesByPolo(processo.tramitacao_atual.partes)
    : null;

  const totalTramitacoes = processo
    ? processo.tramitacoes.length + 1
    : 0;

  const totalDocumentos = processo
    ? processo.tramitacao_atual.documentos.length +
      processo.tramitacoes.reduce((sum, t) => sum + t.documentos.length, 0)
    : 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
        <Scale className="h-4 w-4" />
        <span>Consulta Processual</span>
      </div>

      {isLoading ? (
        <ConsultandoCard />
      ) : error ? (
        <Card>
          <CardContent className="p-4">
            <ErrorContent error={error} />
          </CardContent>
        </Card>
      ) : processo ? (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-base font-bold">
                {processo.numero_processo}
              </CardTitle>
              <Badge variant="secondary" className="text-[0.7rem]">
                {processo.sigla_tribunal}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-3 pb-4">
            {formatPartesResumo(processo.tramitacao_atual.partes) && (
              <p className="text-sm font-medium">
                {formatPartesResumo(processo.tramitacao_atual.partes)}
              </p>
            )}
            {processo.tramitacao_atual.classe[0]?.descricao && (
              <p className="text-sm text-muted-foreground">
                {processo.tramitacao_atual.classe[0].descricao}
              </p>
            )}

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {partiesCount && (
                <span className="flex items-center gap-1">
                  <Users className="h-3.5 w-3.5" />
                  {partiesCount.ativo} polo ativo, {partiesCount.passivo} polo passivo
                </span>
              )}
              <span className="flex items-center gap-1">
                <BookOpen className="h-3.5 w-3.5" />
                {totalTramitacoes} {totalTramitacoes === 1 ? 'tramitação' : 'tramitações'}, {totalDocumentos} {totalDocumentos === 1 ? 'documento' : 'documentos'}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Button
                size="sm"
                onClick={(e) => abrirAutos(processo.numero_processo, { state: { q: searchQuery }, evento: e })}
                onAuxClick={(e) =>
                  e.button === 1 && abrirAutos(processo.numero_processo, { state: { q: searchQuery }, evento: e })
                }
              >
                Ver Autos
              </Button>

              {isTracked ? (
                <Badge variant="outline" className="text-xs">
                  Acompanhando
                </Badge>
              ) : (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={onAcompanhar}
                  disabled={onAcompanharLoading}
                >
                  {onAcompanharLoading && <Spinner className="mr-1 text-inherit" />}
                  + Acompanhar
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : isPreparing ? (
        <ConsultandoCard />
      ) : prepareTimedOut ? (
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col items-start gap-2 py-1">
              <span className="text-sm text-muted-foreground">
                A consulta está demorando mais que o esperado. Você pode
                aguardar um pouco e tentar novamente.
              </span>
              {onRetry && (
                <Button size="sm" variant="outline" onClick={onRetry}>
                  Tentar novamente
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
